import { specificationManualQuoteReasons } from '@misumi/shared'
import { FileTextOutlined, InfoCircleOutlined, RightOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { formatMoney } from '../cart/cart-money'
import type { PartConfiguratorPricingFooterProps } from '../parts/components/part-configurator-pricing'
import type { RfqLineSpecification } from './configuration-estimate'
import { useConfigurationEstimate } from './configuration-estimate'

const QUANTITY_MIN = 1
const QUANTITY_MAX = 1_000_000

/** The shell's generic `PartConfiguratorFormState` mapped onto the RFQ
 * pricing seam's `RfqLineSpecification` — the SAME mapping
 * `RfqLineConfiguratorModal`'s save seam uses (kept in that file; this
 * module only needs the read side for the LIVE estimate, never a save). */
function specificationFromForm(
  form: PartConfiguratorPricingFooterProps['form'],
): RfqLineSpecification {
  return {
    process: form.process,
    material: form.material ?? '',
    finish: form.finish,
    ...(form.inspections && form.inspections.length > 0
      ? { inspections: [...form.inspections] }
      : {}),
    ...(form.certificates && form.certificates.length > 0
      ? { certificates: [...form.certificates] }
      : {}),
    ...(form.customInspection ? { customInspection: true } : {}),
  }
}

/**
 * UID-434/WS-D — RFQ's own implementation of the shell's injected pricing/
 * footer seam (`part-configurator-pricing.ts`). Per the human D4 ruling this
 * ticket carries: RFQ's footer is the LIVE, non-mutating, debounced estimate
 * (`useConfigurationEstimate` -> `POST /pricing/configuration-estimate`),
 * NEVER `subject.indicativePrice`/`subject.dfmSummary` (those are the
 * save-then-reprice shape Parts/Cart use, and are meaningless pre-save
 * here — an RFQ line has no server-derived price until Configure is saved).
 *
 * Mirrors the design's three footer states (`dc.html:5814-5817`), the same
 * ones `ConfigurePartsModal`'s footer already renders for the bulk entry
 * point — kept visually/behaviorally consistent with that sibling rather
 * than inventing a fourth vocabulary for the same underlying
 * `ConfigurationEstimateState`.
 */
export function RfqLineConfiguratorPricingFooter({
  form,
  quantity,
  onQuantityChange,
  onSaveAndClose,
  saving,
  nextPart,
  partSeed,
}: PartConfiguratorPricingFooterProps) {
  // Readiness is MATERIAL-ONLY. `form.finish` cannot be used here to gate
  // this: `PartConfiguratorShell.buildFormState` serializes "As-machined /
  // standard" (the default opening Finish choice, and a complete, non-
  // blocking selection under every rule) to the SAME wire sentinel
  // (`finish: null`) it uses for "no finish touched yet" — so
  // `form.finish !== null` cannot tell those two states apart, and
  // requiring it here blocked "Configure next part" even after a valid,
  // complete selection.
  //
  // The canonical version of this rule already exists and already got this
  // right: `useManufacturingConfigCascade`'s `ready`
  // (`components/manufacturing-config-cascade.ts:223`) is `materialChosen
  // && (!finishRequired || finishChosen)` — material alone suffices unless
  // the chosen material has real secondary finishes AND the raw (pre-
  // serialization) finish state is still untouched. That hook is NOT wired
  // into `PartConfiguratorShell` (this shell keeps its own local
  // material/finish state, never `useManufacturingConfigCascade`), and this
  // footer only receives the shell's already-serialized `form` — which has
  // already collapsed away the distinction the cascade's rule depends on
  // (`finishChosen` vs. "as-machined selected"). Reusing the cascade's rule
  // verbatim would require threading `finishRequired`/`finishChosen` through
  // `PartConfiguratorPricingFooterProps` (a seam shared with Parts/Cart's
  // footer) — disproportionate for this fix. Until that plumbing exists,
  // this is the local (correct) copy: material chosen is the whole gate,
  // matching the shell's own save gate (`buildFormState`/`handleSave` never
  // require a finish either).
  const ready = form.material !== null && form.material.trim() !== ''
  const specification = specificationFromForm(form)
  // Hook called unconditionally, every render, regardless of `ready` — the
  // hook itself no-ops (state `not_ready`) without firing a request when
  // `ready` is false; see its own doc comment.
  //
  // UID-604 / ADR-0177 §4b — `partSeed` comes in as an explicit prop, resolved
  // by `RfqLineConfiguratorModal` through the SHARED `configurationPartSeed`
  // ladder over the same two strings the server snapshots. It is deliberately
  // NOT taken from `subject.displayName` (which this component ignores
  // entirely): that is a client-SYNTHESIZED label — `review-domain.ts` invents
  // `Item <first 8 chars of id>` for a nameless sourceable — and seeding from it
  // would make this footer's figure structurally unable to agree with the figure
  // the save path recomputes, precisely on the lines where identity is weakest.
  const estimateState = useConfigurationEstimate(ready, specification, quantity, partSeed)
  const manualQuoteReasons = specificationManualQuoteReasons(specification)
  const manualQuoteForced =
    manualQuoteReasons.length > 0 || estimateState.kind === 'manual_quote_required'
  // UID-434/WS-D follow-up — "Configure next part". dc.html's priced rail
  // (5700-5704, this component's `!manualQuoteForced` branch below) gates
  // on `inRfqFlow && hasNextPart`; its manual-quote/RFQ rail (5718-5720,
  // the `manualQuoteForced` branch) gates on `hasNextPart` ALONE — looser,
  // since that rail already only exists inside an RFQ flow. This per-line
  // modal is reachable ONLY from an active RFQ draft (`RfqReviewPage`), so
  // there is no real-world case where `inRfqFlow` would be false while this
  // footer renders at all — but the human ruling for this ticket is to
  // apply the STRICTER gate in BOTH rails anyway (never the looser one),
  // modeling "in an RFQ draft flow" as "the caller wired a next-part
  // capability at all" (`nextPart` present), the same optional-callback
  // convention `onAddToCart`/`onSendToRfq` already use on this seam.
  //
  // Gate 5 Suggestion 10 — `nextPart` alone is now the complete gate (it
  // collapses the former separate `onNextPart`/`hasNextPart` pair); no
  // second boolean to AND it against.

  if (!ready) {
    return (
      <div className="part-config-pricing-footer">
        <div className="part-config-pricing-callout part-config-pricing-callout-info">
          <InfoCircleOutlined aria-hidden="true" />
          <p>Configure your part to receive pricing and lead time options.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="part-config-pricing-footer">
      <label className="part-config-pricing-qty">
        <span className="caption-text">Quantity</span>
        <input
          type="number"
          min={QUANTITY_MIN}
          max={QUANTITY_MAX}
          value={quantity}
          onChange={(event) => {
            const next = Number(event.target.value)
            onQuantityChange(Number.isFinite(next) ? next : QUANTITY_MIN)
          }}
        />
      </label>

      {manualQuoteForced ? (
        <div className="part-config-pricing-callout part-config-pricing-callout-violet">
          <FileTextOutlined aria-hidden="true" />
          <p>
            This configuration includes options that need a manual quote. We&apos;ll price it and
            send it back — usually within one business day.
          </p>
        </div>
      ) : estimateState.kind === 'loading' ? (
        <p className="caption-text">Estimating price…</p>
      ) : estimateState.kind === 'estimated' && estimateState.estimate.unitPrice ? (
        <>
          <div className="part-config-pricing-summary-row">
            <div className="part-config-pricing-price">
              <p className="part-config-pricing-price-value">
                {formatMoney(
                  estimateState.estimate.unitPrice.amount,
                  estimateState.estimate.unitPrice.currency,
                )}
              </p>
            </div>
          </div>
          {/* ADR-0117 D7 — this figure is `indicativeUnitPrice`
              (`provenance: parametric`), never a binding price. Plain,
              always-visible text (WCAG — never hover-only), same caveat
              `ConfigurePartsModal`'s footer already carries. */}
          <p className="caption-text part-config-pricing-caveat">
            Preview only — not a final price. Actual pricing is confirmed once your RFQ is quoted.
          </p>
          {/* UID-605 (ADR-0177 Amendment A5) — THE UN-SEEDABLE CASE, caveated.
              `partSeed === undefined` means this part has no identity either
              side can price off: a catalog-SEARCH part (id freshly minted per
              result card, absent from `library.part`, so the SERVER holds
              nothing), a part with no name/SKU/BOM id at all, or the server's
              fail-open library-read degradation (§WAF Reliability). Both sides
              then price at the un-seeded base, so the figure above is honest but
              is the FLOOR rather than this specific part's estimate — and the
              buyer is told, rather than left to discover it when the saved RFQ
              shows a different number.

              This is the honest floor, NOT a substitute for carrying identity:
              every case that CAN be seeded now is (Amendment A2/A4), and this
              renders only for the structural residue.

              Same always-visible `part-config-pricing-caveat` treatment as the
              caveat above — never hover-only, per the WCAG note there.

              Final copy from product design (UID-605), replacing the earlier
              engineering placeholder. */}
          {partSeed === undefined ? (
            <p
              className="caption-text part-config-pricing-caveat"
              data-testid="part-config-seed-caveat"
            >
              Starting estimate — your saved price for this part could be a bit higher.
            </p>
          ) : null}
        </>
      ) : (
        <p className="caption-text">
          We couldn&apos;t estimate this configuration. It can still be saved.
        </p>
      )}

      {/* UID-434/WS-D follow-up — "Configure next part" (dc.html:5700-5704
          priced rail / 5718-5720 manual-quote rail — both call for
          `class="fx-btn-primary"`, i.e. the app's primary-blue treatment,
          not the teal `.part-config-cta-priced` this used to reuse).
          Rendered above "Save and close", same position both dc.html rails
          place it in.

          Now the shared `Button` primitive (`variant="primary"` ->
          `.btn-primary`, `--color-brand-primary` — the same blue token every
          other primary CTA in the app uses) instead of a hand-rolled
          `<button>`. That gets the correct blue for free AND collapses the
          bespoke spinner-affordance copy of `Button.tsx`'s own
          `supportsLoading`/`isLoading` idiom (Gate 5 Suggestion 11's original
          concern) into the one canonical implementation — `loading={saving}`
          here drives the identical spinner/`aria-busy`/`disabled` behavior
          the old raw `<button>` + `Spinner` hand-rolled. `.part-config-cta-
          priced` is intentionally NOT reused here: it's shared with the
          shell's own priced CTA (`PartConfiguratorShell.tsx`), and redefining
          it would have restyled that button too. */}
      {nextPart && (
        <Button
          variant="primary"
          size="lg"
          onClick={nextPart.onAdvance}
          loading={saving}
          className="part-config-cta-next-part"
        >
          Configure next part
          <RightOutlined aria-hidden="true" />
        </Button>
      )}

      <Button
        variant="outline"
        onClick={onSaveAndClose}
        loading={saving}
        className="part-config-cta-secondary"
      >
        Save and close
      </Button>
    </div>
  )
}
